import { Component, Input, forwardRef, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { FillInFieldConfig, FillInField, ParsedTemplate, TemplatePart } from './fill-in-field.interface';

@Component({
  selector: 'app-fill-in-field',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './fill-in-field.component.html',
  styleUrls: ['./fill-in-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FillInFieldComponent),
      multi: true,
    },
  ],
})
export class FillInFieldComponent implements ControlValueAccessor, OnInit, OnChanges {
  @Input() label: string = '';
  @Input() id: string = '';
  @Input() name: string = '';
  @Input() template: string = '';
  @Input() fields: FillInField[] = [];
  @Input() required: boolean = false;
  @Input() preview: boolean = false;

  parsedTemplate: ParsedTemplate = { parts: [] };
  fieldValues: { [key: string]: any } = {};
  value: string = '';

  onChange = (value: any) => {};
  onTouched = () => {};

  ngOnInit() {
    this.parseTemplate();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['template'] || changes['fields']) {
      this.parseTemplate();
    }
  }

  /**
   * 解析模板，將 [fieldId] 標記轉換為填空格
   */
  parseTemplate(): void {
    if (!this.template) {
      this.parsedTemplate = { parts: [] };
      return;
    }

    const parts: TemplatePart[] = [];
    const regex = /\[([^\]]+)\]/g;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(this.template)) !== null) {
      // 添加前面的文字部分
      if (match.index > lastIndex) {
        const textContent = this.template.substring(lastIndex, match.index);
        if (textContent) {
          parts.push({
            type: 'text',
            content: textContent
          });
        }
      }

      // 添加填空格部分
      const fieldId = match[1];
      const fieldConfig = this.fields.find(f => f.id === fieldId);
      parts.push({
        type: 'field',
        content: match[0],
        fieldId: fieldId,
        fieldConfig: fieldConfig
      });

      lastIndex = regex.lastIndex;
    }

    // 添加最後的文字部分
    if (lastIndex < this.template.length) {
      const textContent = this.template.substring(lastIndex);
      if (textContent) {
        parts.push({
          type: 'text',
          content: textContent
        });
      }
    }

    this.parsedTemplate = { parts };
  }

  /**
   * 處理填空格值變更
   */
  onFieldValueChange(fieldId: string, value: any): void {
    this.fieldValues[fieldId] = value;
    this.updateValue();
    this.onTouched();
  }

  /**
   * 更新完整的值
   */
  updateValue(): void {
    if (!this.parsedTemplate.parts.length) {
      this.value = '';
      this.onChange(this.value);
      return;
    }

    let result = '';
    for (const part of this.parsedTemplate.parts) {
      if (part.type === 'text') {
        result += part.content;
      } else if (part.type === 'field' && part.fieldId) {
        const fieldValue = this.fieldValues[part.fieldId];
        result += fieldValue || '';
      }
    }

    this.value = result;
    this.onChange(this.value);
  }

  /**
   * 獲取填空格的 CSS 類別
   */
  getFieldClass(fieldConfig?: FillInField): string {
    if (!fieldConfig) return 'fill-in-field';
    
    const widthClass = fieldConfig.width || 'md';
    return `fill-in-field ${widthClass}`;
  }

  /**
   * 獲取填空格的輸入類型
   */
  getInputType(fieldConfig?: FillInField): string {
    if (!fieldConfig) return 'text';
    return fieldConfig.type;
  }

  /**
   * 獲取填空格的佔位符
   */
  getPlaceholder(fieldConfig?: FillInField): string {
    return fieldConfig?.placeholder || '';
  }

  // ControlValueAccessor 實作
  writeValue(value: any): void {
    this.value = value || '';
    this.parseValueToFields(this.value);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    // 可在此處添加禁用邏輯
  }

  /**
   * 解析值到各個填空格（用於從現有值中恢復填空格內容）
   */
  private parseValueToFields(value: string): void {
    if (!value || !this.template) return;

    // 這裡可以實作從完整字串中解析出各個填空格的值
    // 目前先簡單處理，實際使用時可能需要更複雜的邏輯
    this.fieldValues = {};
  }
}